import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Loader2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useSettings } from "@/lib/settings";
import { useApi, errMsg } from "@/lib/api";

const Block = ({ title, children }) => (
  <div className="border border-white/10 bg-[#0d0d0d] p-5"><p className="font-display uppercase font-extrabold text-xl mb-4">{title}</p>{children}</div>
);

export default function SettingsTab({ isAdmin }) {
  const { settings, reload } = useSettings();
  const api = useApi();
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (settings) setS(JSON.parse(JSON.stringify(settings))); }, [settings]);
  if (!s) return null;

  const ro = !isAdmin;
  const setField = (k, v) => setS({ ...s, [k]: v });
  const setList = (k, i, f, v) => setField(k, s[k].map((x, j) => (j === i ? { ...x, [f]: v } : x)));

  const save = async () => {
    setSaving(true);
    try {
      const prices = Object.fromEntries(Object.entries(s.prices).map(([k, v]) => [k, Number(v) || 0]));
      await api("put", "/admin/settings", { ...s, prices });
      await reload();
      toast.success("Pengaturan disimpan");
    } catch (e) { toast.error(errMsg(e, "Gagal menyimpan")); } finally { setSaving(false); }
  };

  return (
    <div className="grid lg:grid-cols-2 gap-4 mt-4" data-testid="settings-tab">
      <Block title="Rekening Tujuan Transfer">
        <div className="space-y-3">
          {s.bank_accounts.map((b, i) => (
            <div key={i} className="grid grid-cols-[1fr_1.4fr_1.6fr_auto] gap-2" data-testid={`bank-row-${i}`}>
              <Input disabled={ro} value={b.bank} placeholder="Bank" onChange={(e) => setList("bank_accounts", i, "bank", e.target.value)} data-testid={`bank-name-input-${i}`} />
              <Input disabled={ro} value={b.number} placeholder="No. Rekening" onChange={(e) => setList("bank_accounts", i, "number", e.target.value)} data-testid={`bank-number-input-${i}`} />
              <Input disabled={ro} value={b.holder} placeholder="Atas Nama" onChange={(e) => setList("bank_accounts", i, "holder", e.target.value)} data-testid={`bank-holder-input-${i}`} />
              {!ro && <button onClick={() => setField("bank_accounts", s.bank_accounts.filter((_, j) => j !== i))} data-testid={`bank-remove-${i}`} className="px-2 text-neutral-400 hover:text-red-400"><Trash2 className="w-4 h-4" /></button>}
            </div>
          ))}
          {!ro && <button onClick={() => setField("bank_accounts", [...s.bank_accounts, { bank: "", number: "", holder: "" }])} data-testid="bank-add-btn" className="text-sm text-[#FF5A00] flex items-center gap-1"><Plus className="w-4 h-4" /> Tambah rekening</button>}
          <div className="space-y-1 pt-2"><Label>Info pembayaran lain (opsional)</Label><Input disabled={ro} value={s.qris_note} onChange={(e) => setField("qris_note", e.target.value)} placeholder="cth. Bisa juga via QRIS / e-wallet" data-testid="qris-note-input" /></div>
        </div>
      </Block>
      <Block title="Tautan Galeri Google Drive">
        <div className="space-y-3">
          {s.drive_links.map((d, i) => (
            <div key={d.key} className="grid grid-cols-[170px_1fr] gap-2 items-center">
              <Input disabled={ro} value={d.label} onChange={(e) => setList("drive_links", i, "label", e.target.value)} data-testid={`drive-label-input-${d.key}`} />
              <Input disabled={ro} value={d.url} placeholder="https://drive.google.com/..." onChange={(e) => setList("drive_links", i, "url", e.target.value)} data-testid={`drive-url-input-${d.key}`} />
            </div>
          ))}
        </div>
      </Block>
      <Block title="Harga Paket (Rp)">
        <div className="grid grid-cols-3 gap-2">
          {[["1", "1 Mata Lomba"], ["2", "2 Mata Lomba"], ["3", "3/4 Mata Lomba"]].map(([k, l]) => (
            <div key={k} className="space-y-1"><Label>{l}</Label><Input disabled={ro} type="number" value={s.prices[k]} onChange={(e) => setField("prices", { ...s.prices, [k]: e.target.value })} data-testid={`price-input-${k}`} /></div>
          ))}
        </div>
      </Block>
      <Block title="Daftar Mata Lomba">
        <p className="text-xs text-neutral-400 mb-3">Pilihan ini muncul sebagai drop list "Nama Mata Lomba" di form pesanan.</p>
        <div className="space-y-2">
          {s.event_options.map((ev, i) => (
            <div key={i} className="flex gap-2" data-testid={`event-option-row-${i}`}>
              <Input disabled={ro} value={ev} placeholder="cth. Sprint 500m" onChange={(e) => setField("event_options", s.event_options.map((x, j) => (j === i ? e.target.value : x)))} data-testid={`event-option-input-${i}`} />
              {!ro && <button onClick={() => setField("event_options", s.event_options.filter((_, j) => j !== i))} data-testid={`event-option-remove-${i}`} className="px-2 text-neutral-400 hover:text-red-400"><Trash2 className="w-4 h-4" /></button>}
            </div>
          ))}
          {!ro && <button onClick={() => setField("event_options", [...s.event_options, ""])} data-testid="event-option-add-btn" className="text-sm text-[#FF5A00] flex items-center gap-1"><Plus className="w-4 h-4" /> Tambah mata lomba</button>}
        </div>
      </Block>
      <Block title="Info Event & Kontak">
        <div className="grid sm:grid-cols-2 gap-2">
          {[["event_name", "Nama Event"], ["event_venue", "Lokasi"], ["event_date", "Tanggal"], ["whatsapp_number", "WhatsApp Admin"]].map(([k, l]) => (
            <div key={k} className="space-y-1"><Label>{l}</Label><Input disabled={ro} value={s[k]} onChange={(e) => setField(k, e.target.value)} data-testid={`setting-${k}-input`} /></div>
          ))}
        </div>
      </Block>
      {!ro && (
        <button onClick={save} disabled={saving} data-testid="settings-save-btn" className="lg:col-span-2 bg-[#FF5A00] hover:bg-[#E04F00] disabled:opacity-60 py-3 font-display font-black uppercase text-xl flex items-center justify-center gap-2 active:scale-[0.99] transition-[background-color,transform]">
          {saving && <Loader2 className="w-5 h-5 animate-spin" />} Simpan Pengaturan
        </button>
      )}
    </div>
  );
}
